// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import allColors from "@flip/XDL/PreStyle/colors";
import { getAllColorsAsDropdownOptions } from "@flip/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { Option } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import getColor from "@insite/mobius/utilities/getColor";
import React from "react";
import { css } from "styled-components";

const enum fields {
    paddingSize = "paddingSize",
    horizontalRule = "horizontalRule",
    hrHeight = "hrHeight",
    hrColor = "hrColor",
}

interface SpacerProps extends WidgetProps {
    fields: {
        [fields.paddingSize]: string;
        [fields.horizontalRule]: boolean;
        [fields.hrHeight]: number;
        [fields.hrColor]: string;
    };
}

const spacerTemplateWidget: React.FC<SpacerProps> = props => {
    const paddingSize = Number(props.fields.paddingSize);
    let horizontalRule;

    if (props.fields.horizontalRule) {
        // do some validation on the hrHeight Value (1 <= hrHeight <= props.fields.paddingSize)
        let hrHeight = props.fields.hrHeight;
        if (!hrHeight || hrHeight < 1) {
            hrHeight = 1;
        } else if (hrHeight > paddingSize) {
            hrHeight = paddingSize;
        }

        horizontalRule = css`
            &:after {
                content: "";
                display: block;
                width: 100%;
                height: ${hrHeight}px;
                background-color: ${getColor(props.fields.hrColor)};
            }
        `;
    }

    const cssBlock = css`
        ${props.fields.horizontalRule ? `height: ${paddingSize}px` : `margin-bottom: ${paddingSize}px`};
        display: flex;
        flex-direction: column;
        justify-content: center;

        ${horizontalRule}
    `;

    return <StyledWrapper css={cssBlock}></StyledWrapper>;
};

export const definition: WidgetDefinition = {
    group: "Basic",
    fieldDefinitions: [
        {
            name: fields.paddingSize,
            displayName: "Padding Size (px)",
            editorTemplate: "IntegerField",
            fieldType: "General",
            defaultValue: 25,
            min: 1,
        },
        {
            name: fields.horizontalRule,
            displayName: "Show Horizontal Rule",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "General",
        },
        {
            name: fields.hrColor,
            displayName: "Rule Color",
            editorTemplate: "DropDownField",
            options: getAllColorsAsDropdownOptions(allColors),
            isRequired: true,
            defaultValue: "common.ui4",
            fieldType: "General",
            isVisible: (item: HasFields) => item.fields.horizontalRule,
        },
        {
            name: fields.hrHeight,
            displayName: "Rule Height",
            editorTemplate: "IntegerField",
            defaultValue: 1,
            fieldType: "General",
            placeholder: "Enter rule height in px",
            isRequired: true,
            min: 1,
            validate: (value: number | null, item: HasFields) => {
                if (!value || value > Number(item.fields.paddingSize)) {
                    return `Max value is ${item.fields.paddingSize}`;
                }
                return null;
            },
            isVisible: (item: HasFields) => item.fields.horizontalRule,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: spacerTemplateWidget,
    definition,
};

export default widgetModule;
